import React, { useEffect, useState } from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight, 
  Check, 
  X,
  Zap,
  Target,
  Users,
  TrendingUp,
  ChevronDown,
  MessageCircle,
  Search,
  PenTool,
  Rocket,
  Building2,
  Briefcase,
  Award,
  Star,
  Settings,
  Globe,
  MousePointer,
  Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageTracking } from '../../hooks/useAnalytics';
import FooterSection, { PHONE_NUMBER, PHONE_LINK, WHATSAPP_LINK, EMAIL } from '../../components/FooterSection';
import TestimonialsSection from '../../components/TestimonialsSection';

const ProfessionLandingPage = ({ 
  city = "Milano",
  region = "Milano",
  profession = "Avvocati",
  professionId = "avvocati",
  category = "Studi Professionali",
  slug = "siti-web-avvocati-milano",
  metaTitle = "Siti Web per Avvocati Milano | Studio Legale Online",
  metaDescription = "Realizziamo siti web per avvocati e studi legali a Milano. Design professionale, SEO locale, generazione contatti.",
  heroTitle = "Siti web per Avvocati a",
  heroHighlight = "che portano nuovi clienti",
  heroSubtitle = "Presenza online professionale per studi legali. Siti che comunicano competenza, affidabilita e generano contatti qualificati.",
  targetDescription = "studi legali, avvocati civilisti, penalisti, tributaristi",
  painPoints = [],
  benefits = [],
  professionServices = [],
  localAreas = [],
  nearCities = [],
  localContext = ""
}) => {
  const [openFaq, setOpenFaq] = useState(null);
  const pagePath = `/${slug}`;
  const { trackClick } = usePageTracking(pagePath);

  // Handler for CTA clicks
  const handleCtaClick = (type) => {
    trackClick(type);
  };

  const canonicalUrl = `https://www.mm-webdesigners.it/${slug}`;

  useEffect(() => {
    // Update document title
    document.title = metaTitle;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', metaDescription);
    
    // Update keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = `siti web ${profession.toLowerCase()} ${city.toLowerCase()}, ${profession.toLowerCase()} ${city.toLowerCase()} sito internet, web design ${profession.toLowerCase()}, ${region.toLowerCase()}`;
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', metaTitle);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', metaDescription);
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', metaTitle);
    
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', metaDescription);
    
    // Add JSON-LD for this specific page
    let ldScript = document.querySelector('script[data-page-ld]');
    if (!ldScript) {
      ldScript = document.createElement('script');
      ldScript.type = 'application/ld+json';
      ldScript.setAttribute('data-page-ld', 'true');
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": metaTitle,
      "description": metaDescription,
      "url": canonicalUrl,
      "provider": {
        "@type": "ProfessionalService",
        "name": "M&M Web Designers",
        "url": "https://www.mm-webdesigners.it/"
      },
      "areaServed": {
        "@type": "City",
        "name": city
      },
      "serviceType": `Web Design per ${profession}`
    });
    
    return () => {
      const pageLd = document.querySelector('script[data-page-ld]');
      if (pageLd) pageLd.remove();
    };
  }, [metaTitle, metaDescription, canonicalUrl, city, region, slug, profession]);

  const processSteps = [
    { 
      icon: <Search size={24} />, 
      title: "Analisi Settore", 
      desc: `Studiamo il mercato dei ${profession.toLowerCase()} a ${city}, i competitor e cosa cercano i potenziali clienti` 
    },
    { 
      icon: <PenTool size={24} />, 
      title: "Progettazione", 
      desc: "Definiamo struttura, contenuti e design specifico per il tuo settore professionale" 
    },
    { 
      icon: <Settings size={24} />, 
      title: "Sviluppo", 
      desc: "Realizziamo il sito con funzionalita dedicate alla tua professione" 
    },
    { 
      icon: <Rocket size={24} />, 
      title: "Lancio", 
      desc: "Mettiamo online il sito ottimizzato per farti trovare dai tuoi potenziali clienti" 
    }
  ];

  const results = [
    { icon: <Eye size={22} />, title: "Visibilita di settore", desc: `Compari quando cercano ${profession.toLowerCase()} a ${city}` },
    { icon: <MousePointer size={22} />, title: "Contatti qualificati", desc: "Richieste da potenziali clienti gia interessati ai tuoi servizi" },
    { icon: <Globe size={22} />, title: "Immagine professionale", desc: "Un sito che riflette la qualita e competenza del tuo lavoro" },
    { icon: <TrendingUp size={22} />, title: "Crescita costante", desc: "Presenza online che genera risultati nel tempo" }
  ];

  const differences = [
    { wrong: "Siti generici", right: "Sito specifico per " + profession.toLowerCase() },
    { wrong: "Template standard", right: "Design su misura per il tuo settore" },
    { wrong: "SEO generica", right: "SEO mirata alla tua professione" },
    { wrong: "Zero supporto", right: "Assistenza continua dedicata" }
  ];

  const faqs = [
    {
      q: `Quanto costa un sito web per ${profession.toLowerCase()} a ${city}?`,
      a: `Il costo varia in base alle funzionalita richieste. Un sito professionale per ${profession.toLowerCase()} parte da circa 1.200-2.500€, incluse ottimizzazione SEO, form contatti avanzati e design personalizzato per il tuo settore. Ti forniamo sempre un preventivo dettagliato dopo aver capito le tue esigenze specifiche.`
    },
    {
      q: `Perche un sito specifico per ${profession.toLowerCase()}?`,
      a: `Ogni professione ha esigenze diverse. Un sito per ${profession.toLowerCase()} deve comunicare competenza nel settore, rispettare eventuali vincoli deontologici, e presentare i servizi in modo che i potenziali clienti capiscano immediatamente come puoi aiutarli. Un sito generico non puo fare tutto questo.`
    },
    {
      q: "Come mi fate trovare su Google?",
      a: `Ottimizziamo il sito per ricerche specifiche come "${profession.toLowerCase()} ${city}", "${profession.toLowerCase()} zona + servizio specifico". Lavoriamo su contenuti, struttura tecnica e SEO locale per posizionarti davanti ai competitor nelle ricerche dei tuoi potenziali clienti.`
    },
    {
      q: "In quanto tempo avro il sito pronto?",
      a: "Per un sito professionale di qualita, i tempi sono di 3-4 settimane dalla conferma. Questo include analisi, progettazione, sviluppo e ottimizzazione. Ti teniamo aggiornato ad ogni fase del processo."
    },
    {
      q: "Posso gestire il sito in autonomia?",
      a: "Assolutamente si. Ti forniamo un pannello di gestione semplice per aggiornare contenuti, aggiungere news o articoli, e gestire le richieste di contatto. Per modifiche strutturali, interveniamo noi."
    },
    {
      q: "Offrite assistenza dopo la pubblicazione?",
      a: "Si, sempre. Offriamo pacchetti di manutenzione che includono aggiornamenti, backup, supporto tecnico e piccole modifiche. Non ti lasciamo mai solo dopo la messa online."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/logo.png" 
              alt="M&M Web Designers" 
              className="h-12 w-auto"
              data-testid="header-logo"
            />
          </Link>
          <div className="flex items-center gap-4">
            <a 
              href={PHONE_LINK}
              className="hidden md:flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              <Phone size={16} />
              {PHONE_NUMBER}
            </a>
            <Link 
              to="/"
              className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
            >
              Vai al Sito Principale
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-[#0ea5e9]" />
              <span className="text-white/70 text-sm">{city}, {region}</span>
            </div>
            <span className="bg-[#0ea5e9]/20 text-[#0ea5e9] text-xs px-3 py-1 rounded-full">
              {category}
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {heroTitle} {city}{" "}
            <span className="text-[#0ea5e9]">{heroHighlight}</span>
          </h1>
          
          <p className="text-white/60 text-lg sm:text-xl mb-8 max-w-3xl leading-relaxed">
            {heroSubtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="/#contatti" 
              onClick={() => handleCtaClick('contact_click')}
              className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2"
            >
              Richiedi Demo Gratuita
              <ArrowRight size={18} />
            </a>
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCtaClick('whatsapp_click')}
              className="border border-white/20 hover:border-[#0ea5e9] text-white px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle size={18} />
              Scrivici su WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* PERCHE UN SITO WEB PER LA TUA PROFESSIONE */}
      <section className="py-16 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Perche i {profession.toLowerCase()} di {city} hanno bisogno di un sito web
          </h2>
          
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>
              Oggi, quando qualcuno cerca un {profession.toLowerCase().slice(0, -1)} a {city}, la prima cosa che fa e aprire Google. 
              Se non hai un sito professionale, semplicemente non esisti per questi potenziali clienti.
            </p>
            <p>
              Ma non basta un sito qualsiasi. Un sito per {targetDescription} deve comunicare 
              <strong className="text-white"> competenza, affidabilita e professionalita</strong>. 
              Deve rispondere alle domande dei visitatori e guidarli verso il contatto.
            </p>
            <p>
              I tuoi competitor lo stanno gia facendo. E tu?
            </p>
          </div>

          {/* Mini CTA */}
          <div className="mt-8 p-6 bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 rounded-xl">
            <p className="text-white/80 mb-4">
              Vuoi vedere come potrebbe essere il tuo sito? Ti prepariamo una proposta gratuita.
            </p>
            <a 
              href="/#contatti" 
              className="inline-flex items-center gap-2 text-[#0ea5e9] font-medium hover:underline"
            >
              Richiedi una proposta gratuita <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* PROBLEMI */}
      {painPoints.length > 0 && (
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ti riconosci in queste situazioni?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {painPoints.map((problem, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-[#111] rounded-xl border border-white/5">
                  <X size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/80">{problem}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-white/60">
              Se hai risposto si anche solo a una di queste, possiamo aiutarti. 
              Ecco come risolviamo questi problemi.
            </p>
          </div>
        </section>
      )}

      {/* COME LAVORIAMO */}
      <section className="py-16 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Come creiamo il tuo sito
          </h2>
          <p className="text-white/60 mb-10">
            Un processo pensato per le esigenze specifiche dei {profession.toLowerCase()}.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center p-6 bg-white/5 rounded-xl border border-white/5 h-full">
                  <div className="w-12 h-12 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center text-[#0ea5e9] mb-4">
                    {step.icon}
                  </div>
                  <span className="text-[#0ea5e9] text-sm font-medium mb-2">Step {index + 1}</span>
                  <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-white/60 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      {benefits.length > 0 && (
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Cosa ottieni con il nostro sito
            </h2>
            <p className="text-white/60 mb-10">
              Funzionalita pensate specificamente per {profession.toLowerCase()}.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 p-5 border border-white/10 rounded-xl hover:border-[#0ea5e9]/30 transition-colors bg-[#111]">
                  <div className="w-10 h-10 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0">
                    <Check size={20} className="text-[#0ea5e9]" />
                  </div>
                  <div>
                    <p className="text-white/80">{benefit}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVIZI DELLA PROFESSIONE */}
      {professionServices.length > 0 && (
        <section className="py-16 px-6 bg-[#111]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Siti per ogni specializzazione
            </h2>
            <p className="text-white/60 mb-8">
              Creiamo siti specifici per {profession.toLowerCase()} specializzati in:
            </p>
            <div className="flex flex-wrap gap-3">
              {professionServices.map((service, index) => (
                <span 
                  key={index}
                  className="bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 text-white px-5 py-3 rounded-full"
                >
                  {service}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RISULTATI */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Risultati concreti per i {profession.toLowerCase()}
          </h2>
          <p className="text-white/60 mb-10">
            Ecco cosa puoi realisticamente aspettarti dal tuo nuovo sito.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {results.map((result, index) => (
              <div key={index} className="flex items-start gap-4 p-5 border border-white/10 rounded-xl bg-[#111]">
                <div className="w-10 h-10 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0 text-[#0ea5e9]">
                  {result.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{result.title}</h3>
                  <p className="text-white/60 text-sm">{result.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENZIAZIONE */}
      <section className="py-16 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Perche scegliere noi per il sito del tuo studio
          </h2>
          <p className="text-white/60 mb-10">
            Non siamo un&apos;agenzia generica. Conosciamo le esigenze dei {profession.toLowerCase()}.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {differences.map((diff, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-red-400/70 text-sm mb-1">
                    <X size={14} />
                    <span className="line-through">{diff.wrong}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#0ea5e9]">
                    <Check size={14} />
                    <span className="font-medium">{diff.right}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONE LOCALE */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {profession} a {city}: ti facciamo trovare
          </h2>
          <div className="space-y-4 text-white/70 leading-relaxed mb-8">
            <p>
              {localContext}, {city} offre molte opportunita per i {profession.toLowerCase()} 
              che sanno farsi trovare online.
            </p>
            <p>
              Ottimizziamo il tuo sito per le ricerche locali: quando qualcuno cerca 
              &quot;{profession.toLowerCase()} {city}&quot; o servizi specifici nella tua zona, 
              il tuo sito comparira nei risultati.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-8">
            {localAreas.length > 0 && (
              <div>
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin size={18} className="text-[#0ea5e9]" />
                  Zone di {city} che copriamo
                </h3>
                <div className="flex flex-wrap gap-2">
                  {localAreas.map((area, index) => (
                    <span key={index} className="bg-[#111] border border-white/10 px-3 py-1.5 rounded-full text-sm text-white/70">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {nearCities.length > 0 && (
              <div>
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Target size={18} className="text-[#0ea5e9]" />
                  Comuni limitrofi
                </h3>
                <div className="flex flex-wrap gap-2">
                  {nearCities.map((nearCity, index) => (
                    <span key={index} className="bg-[#111] border border-white/10 px-3 py-1.5 rounded-full text-sm text-white/70">
                      {nearCity}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Domande frequenti
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-white/10 rounded-xl overflow-hidden bg-[#0a0a0a]"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.q}</span>
                  <ChevronDown 
                    size={20} 
                    className={`text-[#0ea5e9] flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-white/60 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIANZE */}
      <TestimonialsSection compact={true} />

      {/* CTA FINALE */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0a0a0a] to-[#111]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pronto a far crescere il tuo studio?
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Raccontaci del tuo studio e dei tuoi obiettivi. Ti rispondiamo entro 24 ore con una proposta su misura.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/#contatti" 
              onClick={() => handleCtaClick('contact_click')}
              className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2"
            >
              Richiedi Demo Gratuita
              <ArrowRight size={18} />
            </a>
            <a 
              href={PHONE_LINK}
              onClick={() => handleCtaClick('phone_click')}
              className="border border-white/20 hover:border-[#0ea5e9] text-white px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              {PHONE_NUMBER}
            </a>
          </div>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-white/50 text-sm">
            <span>Oppure scrivici:</span>
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCtaClick('whatsapp_click')}
              className="flex items-center gap-2 hover:text-[#25D366] transition-colors"
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
            <a 
              href={`mailto:${EMAIL}`}
              onClick={() => handleCtaClick('email_click')}
              className="flex items-center gap-2 hover:text-[#0ea5e9] transition-colors"
            >
              <Mail size={16} />
              {EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <FooterSection showNewsletter={false} />
    </div>
  );
};

export default ProfessionLandingPage;
